<template>
    <loading-indicator v-if="loading"/>
    <component v-else :is="componentOptions.is" v-bind="componentOptions" :additional-data="additionalData"/>
</template>

<script setup lang="ts">
    import {useCalendar} from '@/composables/modules/calendar/calendar';
    import {usePortuna} from '@/composables/portuna';
    import axios from 'axios';
    import LoadingIndicator from '@/components/LoadingIndicator.vue';
    import {popupDefaultCloseButton} from '@/library/popup';
    import type {TinyEmitter} from 'tiny-emitter';
    import AppointmentEntryViewContent from '../../appointment/entry/AppointmentEntryViewContent.vue';
    import CodeReviewAppointmentViewPopup from '../../codereview/appointment/CodeReviewAppointmentViewPopup.vue';
    import EnrolmentAppointmentPopupView from '../../enrolment/appointment/components/EnrolmentAppointmentPopupView.vue';
    import EventEntryViewContent from '../../event/entry/EventEntryViewContent.vue';
    import ServicesEntryViewContent from '../../services/calendar/entry/ServicesEntryViewContent.vue';
    import ShiftAppointmentViewPopup from '../../shift-schedule/appointment/ShiftAppointmentViewPopup.vue';
    import TrainingAppointmentPopupView from '../../training/appointment/components/TrainingAppointmentPopupView.vue';
    import CalendarEntryViewContent from './CalendarEntryViewContent.vue';
    import {ref, computed, toRefs, inject} from 'vue';

    const popupEmitter = inject<TinyEmitter>('popup-emitter')!;

    const portuna = usePortuna();

    const props = withDefaults(
        defineProps<{
            entry: CalendarEntryData;
            calendar?: CalendarData;
            resource: CalendarResources;
            additionalData?: AdditionalCalendarData;
        }>(),
        {
            additionalData: () => ({}),
        },
    );

    const {
              resource,
              additionalData,
          } = toRefs(props);

    const {
              categoryManager,
          } = useCalendar(resource);

    const loading  = ref(true);
    const viewData = ref<Record<string, any>>({});

    const id = computed(() =>
    {
        return props.entry.id;
    });

    const start = computed(() =>
    {
        return props.entry.recurrence_start || '';
    });

    const entryResource = computed(() =>
    {
        let resource: string = props.resource;
        if (props.entry.resource)
        {
            resource = props.entry.resource;
        }

        return resource;
    });

    const componentOptions = computed(() =>
    {
        const newViewData = Object.assign({}, viewData.value!);
        if ('undefined' !== typeof newViewData.is)
        {
            return newViewData;
        }
        if ('enrolment' === entryResource.value)
        {
            newViewData.is = EnrolmentAppointmentPopupView;
        }
        else if ('training' === entryResource.value)
        {
            newViewData.is              = TrainingAppointmentPopupView;
            newViewData.categoryManager = categoryManager;
        }
        else if ('shift-schedule' === entryResource.value)
        {
            newViewData.is = ShiftAppointmentViewPopup;
        }
        else if ('code-review' === entryResource.value)
        {
            newViewData.is = CodeReviewAppointmentViewPopup;
        }
        else if ('appointment' === entryResource.value)
        {
            newViewData.is = AppointmentEntryViewContent;
        }
        else if ('event' === entryResource.value)
        {
            newViewData.is = EventEntryViewContent;
        }
        else if ('services' === entryResource.value)
        {
            newViewData.is = ServicesEntryViewContent;
        }
        else
        {
            newViewData.is = CalendarEntryViewContent;
        }

        return newViewData;
    });

    /**
     * resource not allowed fallback -> show only already available data
     */
    const notAllowedFallback = () =>
    {
        popupEmitter.emit('setButtons', [popupDefaultCloseButton]);

        viewData.value.is       = CalendarEntryViewContent;
        viewData.value.entry    = props.entry;
        viewData.value.calendar = props.calendar;
        viewData.value.resource = props.resource;
        loading.value           = false;
    };

    const fetchEntry = () =>
    {
        if ('enrolment' === entryResource.value)
        {
            axios
            .get('/employee/enrolment/appointment/fetch-view/id/' + id.value)
            .then(({data: {data}}) =>
            {
                viewData.value.entry       = data.entry;
                viewData.value.application = data.application;
                viewData.value.enrolment   = data.enrolment;
                viewData.value.isAllowed   = data.isAllowed;
            })
            .catch(notAllowedFallback)
            .finally(() =>
            {
                loading.value = false;

                popupEmitter.emit('lazy-load-focus');
            });
        }
        else if ('training' === entryResource.value)
        {
            axios
            .get('/employee/training/appointment/fetch-popup-view/id/' + id.value)
            .then(({data: {data}}) =>
            {
                viewData.value.entry = data.entry;

                categoryManager.setSelectCategories(data.categories);
            })
            .catch(notAllowedFallback)
            .finally(() =>
            {
                loading.value = false;

                popupEmitter.emit('lazy-load-focus');
            });
        }
        else if ('shift-schedule' === entryResource.value)
        {
            axios
            .get('/employee/shift-schedule/calendar/fetch-shift-info/shift-id/' +
                id.value +
                '/function-id/0/date/' +
                start.value,
            )
            .then(({data: {data}}) =>
            {
                viewData.value.entry             = props.entry;
                viewData.value.assignedEmployees = data.assigned_employees;
            })
            .catch(notAllowedFallback)
            .finally(() =>
            {
                loading.value = false;

                popupEmitter.emit('lazy-load-focus');
            });
        }
        else if (['roommanagement', 'vehicle', 'sportsarea', 'equipment', 'appointment', 'event'].includes(entryResource.value))
        {
            axios
            .get(
                '/' +
                entryResource.value +
                '/entry/fetch/id/' +
                id.value +
                (start.value ? '/start/' + start.value : ''),
            )
            .then(({data: {data}}) =>
            {
                viewData.value.entry    = data.entry;
                viewData.value.calendar = data.calendar;
                viewData.value.resource = entryResource.value;
            })
            .catch(notAllowedFallback)
            .finally(() =>
            {
                loading.value = false;

                popupEmitter.emit('lazy-load-focus');
            });
        }
        else
        {
            viewData.value.entry    = props.entry;
            viewData.value.calendar = props.calendar;
            viewData.value.resource = props.resource;
            loading.value           = false;
        }
    };

    fetchEntry();

    defineExpose(Object.assign({}, portuna));
</script>